import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LogOut, MonitorSmartphone, ArrowRight } from "lucide-react";

import { fetchSessions, revokeSession } from "../api/users";
import { explainError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { shortDate, fmtIST } from "../lib/format";

/* ──────────────────────────────────────────────────────────────────────
   /users/sessions — every live login session (one per account, enforced
   at login). Modeled on the simpleey project's SessionsSettings page:
   who, from which IP, on what browser, since when — with a revoke button.
   Auto-refreshes every 30 s. SuperAdmin-only.
   ────────────────────────────────────────────────────────────────────── */

function browserLabel(ua: string | null | undefined): string {
  if (!ua) return "—";
  if (/edg\//i.test(ua)) return "Edge";
  if (/chrome\//i.test(ua)) return "Chrome";
  if (/firefox\//i.test(ua)) return "Firefox";
  if (/safari\//i.test(ua)) return "Safari";
  if (/python-requests|curl/i.test(ua)) return "Script / API";
  return ua.slice(0, 40);
}

function osLabel(ua: string | null | undefined): string {
  if (!ua) return "";
  if (/windows/i.test(ua)) return "Windows";
  if (/mac os x|macintosh/i.test(ua)) return "macOS";
  if (/android/i.test(ua)) return "Android";
  if (/iphone|ipad/i.test(ua)) return "iOS";
  if (/linux/i.test(ua)) return "Linux";
  return "";
}

const STATUS_STYLE: Record<string, { label: string; color: string }> = {
  active:   { label: "live",     color: "var(--primary)" },
  replaced: { label: "replaced", color: "var(--fg-muted)" },
  revoked:  { label: "revoked",  color: "var(--accent)" },
  expired:  { label: "expired",  color: "var(--fg-faint)" },
};

export function UserSessions() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const [history, setHistory] = useState(false);

  const q = useQuery({
    queryKey: ["users/sessions", history],
    queryFn: () => fetchSessions(history),
    refetchInterval: 30_000,
  });

  const revoke = useMutation({
    mutationFn: (sessionId: number) => revokeSession(sessionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users/sessions"] }),
  });

  return (
    <div className="p-6 max-w-[1080px] mx-auto">
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] mb-1">
            Administration · Sessions
          </div>
          <h1 className="font-display text-[28px] leading-tight">
            {history ? "Session history" : "Active sessions"}
          </h1>
          <p className="font-sans text-[13px] text-[var(--fg-muted)] mt-1 max-w-[640px]">
            One live session per account. Revoking signs that user out within a few
            minutes (their next token refresh fails). When a session rotates its
            token, the old one is marked <strong>replaced&nbsp;→</strong> the new one.
          </p>
        </div>
        <button
          onClick={() => setHistory(h => !h)}
          className="np-btn-ghost text-[11px] whitespace-nowrap"
          title="Toggle the rotation / revocation history"
        >
          {history ? "Show live only" : "Show history"}
        </button>
      </header>

      {q.isLoading && <div className="font-mono text-[11px] text-[var(--fg-faint)]">loading…</div>}
      {q.isError && (
        <div className="np-banner">
          <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--accent)] mr-2">Error</span>
          {explainError(q.error)}
        </div>
      )}

      {q.data && q.data.length === 0 && (
        <div className="font-mono text-[11px] text-[var(--fg-faint)]">
          {history ? "No sessions recorded." : "No live sessions."}
        </div>
      )}

      {q.data && q.data.length > 0 && (
        <div className="np-card overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--rule-strong)" }}>
                <Th>User</Th>
                <Th>Status</Th>
                <Th>IP address</Th>
                <Th>Device</Th>
                <Th>Signed in</Th>
                <Th>{history ? "Ended" : "Expires"}</Th>
                <Th className="text-right pr-4">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {q.data.map(s => {
                const isMine = s.user_id === (me?.user_id ?? -1);
                const st = STATUS_STYLE[s.status] ?? STATUS_STYLE.active!;
                return (
                  <tr key={s.session_id} style={{ borderTop: "1px solid var(--rule)" }}>
                    <Td>
                      <div className="font-display text-[14px]">
                        {s.username}
                        {isMine && s.status === "active" &&
                          <span className="np-chip np-chip--admin ml-2">this session</span>}
                      </div>
                      <div className="font-mono text-[10px] text-[var(--fg-faint)] mt-0.5">
                        session #{s.session_id}
                      </div>
                    </Td>
                    <Td>
                      <span className="font-mono text-[10px] uppercase tracking-folio" style={{ color: st.color }}>
                        {st.label}
                      </span>
                      {s.replaced_by_session_id != null && (
                        <div className="font-mono text-[10px] text-[var(--fg-faint)] mt-0.5 inline-flex items-center gap-0.5">
                          <ArrowRight size={9} /> #{s.replaced_by_session_id}
                        </div>
                      )}
                    </Td>
                    <Td><span className="font-mono text-[11px]">{s.ip ?? "—"}</span></Td>
                    <Td>
                      <div className="font-mono text-[11px]">
                        <MonitorSmartphone size={10} className="inline mr-1 text-[var(--fg-faint)]" />
                        {browserLabel(s.user_agent)}
                        {osLabel(s.user_agent) && (
                          <span className="text-[var(--fg-muted)]"> · {osLabel(s.user_agent)}</span>
                        )}
                      </div>
                    </Td>
                    <Td>
                      <span className="font-mono text-[11px]">
                        {s.issued_at_utc ? <>{shortDate(s.issued_at_utc)} · {fmtIST(s.issued_at_utc)}</> : "—"}
                      </span>
                    </Td>
                    <Td>
                      <span className="font-mono text-[11px] text-[var(--fg-muted)]">
                        {s.status !== "active" && s.revoked_at_utc
                          ? <>{shortDate(s.revoked_at_utc)} · {fmtIST(s.revoked_at_utc)}</>
                          : s.expires_at_utc ? shortDate(s.expires_at_utc) : "—"}
                      </span>
                    </Td>
                    <Td className="text-right pr-4">
                      {s.status === "active" ? (
                        <button
                          onClick={() => revoke.mutate(s.session_id)}
                          disabled={revoke.isPending}
                          className="np-btn-ghost text-[11px] whitespace-nowrap"
                          title={isMine
                            ? "Revoking your own session signs you out"
                            : "Sign this user out"}
                        >
                          <LogOut size={10} className="inline mr-1" />Revoke
                        </button>
                      ) : (
                        <span className="font-mono text-[10px] text-[var(--fg-faint)]">—</span>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {revoke.isError && (
        <div className="np-banner mt-3">
          <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--accent)] mr-2">Error</span>
          {explainError(revoke.error)}
        </div>
      )}
    </div>
  );
}

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`text-left px-3 py-2 font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] ${className}`}>
      {children}
    </th>
  );
}
function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;
}
